import React, { useEffect, useState } from 'react';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import { HomeOutlined, EditOutlined, DeleteFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Pagination,
  Popconfirm,
  ConfigProvider,
  BackTop,
} from 'antd';
import { Table, Space, Tooltip, Button } from 'antd';
import TableDateRow from 'modules/layoutModule/components/TableDateRow/TableDateRow';
import plPL from 'antd/lib/locale/pl_PL';
import moment from 'moment';
import TableDataRowTypes from 'modules/layoutModule/enums/TableDataRowTypes';
import useColumnSearch from 'modules/layoutModule/hooks/useColumnSearch';
import {
  getFertilizersRequest,
  removeSpecificFertilizersRequest,
  getFertilizersCategoriesRequest,
} from '../requests/fertilizersRequests';

export interface NewsTableTypes {
  id: number;
  dateAdd: moment.MomentInput;
  name: string;
  isActive: number;
  deleteDisabled?: boolean;
}

const Fertilizers = (): JSX.Element => {
  const [tableData, setTableData] = useState<NewsTableTypes[]>([]);
  const [loading, toggleLoading] = useState<boolean>(true);
  const [searchedColumn, setSearchedColumn] = useState('');
  const [tableKey, setTableKey] = useState(0);
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);

  const resetFilters = () => {
    setTableKey((tableKey) => tableKey + 1);
    setSearchedColumn('');
  };

  const confirmDelete = (id: number) => {
    removeSpecificFertilizersRequest(id).then(() => {
      downloadList();
    });
  };

  useEffect(() => {
    if (tableData.length === 0) {
      downloadList();
    }
  }, []);

  const downloadList = () =>
    getFertilizersRequest().then((response) => {
      if (response) {
        const { data } = response;
        setTableData(data);
        toggleLoading(false);
      } else {
        toggleLoading(false);
      }
    });

  useEffect(() => {
    if (categories.length === 0) {
      getFertilizersCategoriesRequest().then((response) => {
        if (response) {
          const { data } = response;
          const mapCategories = data.map((item: any) => ({
            text: `${item.name} - ${item.subCatName}`,
            value: item.id,
          }));
          setCategories(mapCategories);
        }
      });
    }
  }, [categories]);

  const columns = [
    {
      title: 'Nazwa',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
      ...useColumnSearch(
        'name',
        'Szukaj po nazwie',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn,
        resetFilters
      ),
      render: (_: any, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.NAME_LINK}
          link={`nawozy/${record.categoryUrl}/${record.subCategoryUrl}/${record.url}`}
        />
      ),
    },
    {
      title: 'Opakowanie główne',
      dataIndex: 'image1',
      key: 'image1',
      width: 100,
      render: (_: any, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.FERTILIZER_IMAGE}
          fieldName="image1"
          size={100}
          customHeight
          isPng
        />
      ),
    },

    {
      title: 'Kategoria',
      dataIndex: 'category',
      key: 'category',
      width: 160,
      sorter: (a: { category: number }, b: { category: number }) =>
        a.category - b.category,
      filters: categories,
      onFilter: (value: string, record: any) => {
        return record.category === value;
      },
      render: (_: string, record: any) => {
        return (
          <TableDateRow
            record={record}
            field={TableDataRowTypes.CATEGORY_TAG}
            fieldName="categoryName"
          />
        );
      },
    },
    {
      title: 'Podkategoria',
      dataIndex: 'subCatName',
      key: 'subCatName',
      width: 160,
      render: (text: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.CATEGORY_TAG}
          fieldName="subCatName"
        />
      ),
    },

    {
      title: 'Kolejność',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 100,
      sorter: (a: any, b: any) => a.sortOrder - b.sortOrder,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.SORT} />
      ),
    },
    {
      title: 'Licznik',
      dataIndex: 'viewCount',
      width: 100,
      sorter: (a: { viewCount: number }, b: { viewCount: number }) =>
        a.viewCount - b.viewCount,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.VIEWS} />
      ),
    },
    {
      title: 'Aktywna',
      dataIndex: 'isActive',
      key: 'isActive',
      width: '100px',
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.IS_ACTIVE} />
      ),
      sorter: (a: { isActive: number }, b: { isActive: number }) =>
        a.isActive - b.isActive,
      filters: [
        {
          text: 'TAK',
          value: 'isActive',
        },
        {
          text: 'NIE',
          value: 'isNotActive',
        },
      ],
      onFilter: (value: string, record: { isActive: boolean }) => {
        if (value === 'isActive') {
          return record.isActive;
        }
        return !record.isActive;
      },
    },
    {
      title: 'Akcje',
      dataIndex: 'operation',
      key: 'operation',
      width: 100,
      render: (text: string, item: NewsTableTypes) => {
        return (
          <Space
            size="middle"
            style={{ display: 'Flex', flexDirection: 'column' }}
          >
            <Tooltip title="Edytuj wpis">
              <Link to={`/fertilizers/${item.id}`}>
                <Button type="primary" shape="circle" icon={<EditOutlined />} />
              </Link>
            </Tooltip>
            <Tooltip title="Usuń wpis">
              <Popconfirm
                title="Czy na pewno chcesz usunąc ten wpis?"
                placement="left"
                onConfirm={() => confirmDelete(item.id)}
                okText="Tak"
                cancelText="Nie"
                disabled={item.isActive === 1}
              >
                <Button type="link" disabled={item.isActive === 1}>
                  <DeleteFilled />
                </Button>
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Nawozy</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/fertilizers">Lista</Link>
          </Breadcrumb.Item>
        </>
      </Bread>

      <ConfigProvider locale={plPL}>
        <Table
          // @ts-ignore
          columns={columns}
          // @ts-ignore
          dataSource={tableData}
          loading={loading}
          rowKey={(record) => record.id}
          key={tableKey}
        >
          <Pagination />
        </Table>
        <BackTop />
      </ConfigProvider>
    </>
  );
};

export default Fertilizers;
